import { findCompanionModel } from "@/lib/companion-model-lookup";

/**
 * Per-model vision capability.
 *
 * Used to gate screenshot capture (`use-screenshot-capture`) and image
 * attachments. The backend also strips images defensively in the
 * message-create serializer.
 *
 * The CLI catalog the companion publishes decides first (Codex and OpenCode
 * report image input). This set covers Claude, whose catalog doesn't say,
 * and sessions without a catalog. Server-side mirror lives at
 * `backend/apps/ai/vision_capabilities.py`. Unknown ids default to `false`
 * (fail closed).
 */

const VISION_MODELS: ReadonlySet<string> = new Set([
  // Claude — every 3.5+ model supports vision
  "default", "best", "opus", "opus[1m]", "opusplan",
  "sonnet", "sonnet[1m]", "haiku",
  "claude-fable-5-1", "claude-fable-5",
  "claude-opus-5-5", "claude-opus-4-8", "claude-opus-4-8[1m]",
  "claude-opus-4-7", "claude-opus-4-7[1m]",
  "claude-opus-4-5", "claude-opus-4-1",
  "claude-sonnet-4-6", "claude-sonnet-4-6[1m]",
  "claude-sonnet-5-5", "claude-sonnet-5",
  "claude-sonnet-4-5", "claude-sonnet-4",
  "claude-3-5-haiku", "claude-haiku-4-5", "claude-haiku-4-5-20251001",
  // OpenAI — GPT-5 family supports vision EXCEPT codex-spark and nano
  "gpt-6.1-sol", "gpt-6-sol", "gpt-6-astra", "gpt-6-luna",
  "gpt-5.6-sol", "gpt-5.6-terra", "gpt-5.6-luna",
  "gpt-5.5",
  "gpt-5.4", "gpt-5.4-pro", "gpt-5.4-mini",
  "gpt-5.3-codex", "gpt-5.2", "gpt-5.2-codex",
  "gpt-5.1", "gpt-5.1-codex", "gpt-5.1-codex-max", "gpt-5.1-codex-mini",
  "gpt-5", "gpt-5-codex",
  // Google — Gemini 3 family is multimodal
  "gemini-3.1-pro", "gemini-3-pro", "gemini-3-flash",
  // Paid multimodal
  "minimax-m3", "opencode/minimax-m3", "openrouter/minimax/minimax-m3",
  "mimo-v2.5-free", "opencode/mimo-v2.5-free",
  "openrouter/qwen/qwen3.8-flash", "openrouter/qwen/qwen3.6-plus",
  "qwen3.8-flash", "qwen3.6-plus",
  "minimax-m2.1",
  "glm-4.7",
  "kimi-k2.5", "kimi-k2", "kimi-k2-thinking",
]);

export function modelSupportsVision(modelId: string | null | undefined): boolean {
  if (!modelId) return false;
  const published = findCompanionModel(modelId)?.image_input;
  if (typeof published === "boolean") return published;
  // OpenCode Zen and Go ids name the same model as the bare id.
  const bare = modelId.replace(/^opencode(-go)?\//, "");
  return VISION_MODELS.has(modelId) || VISION_MODELS.has(bare);
}
